import { useIsMobile, WSFlexBox } from "@wingspanhq/fe-component-library";
import * as React from "react";
import { useFocusedFeatures } from "../../../FocusedFeatures";
import { useCustomization } from "../../../modules/customization";
import { useQueryNotifications } from "../../../query/notifications/queries/useQueryNotifications";
import { selectorCountOfVisibleNotifications } from "../../../shared/selectors/selectorCountOfVisibleNotifications";
import pluralize from "pluralize";
import { CollapsibleMenu } from "../CollapsibleMenu";
import { New } from "../New";
import { SideNavMenuItem } from "../SideNavMenuItem";
import { SideNavRootLinkOption } from "../SideNavRootLinkOption";
import styles from "../styles.module.scss";

import {
  PATH_BENEFITS_AXIS_INSURANCE,
  PATH_BENEFITS_INSURANCE,
  PATH_BENEFITS_PERKS
} from "../../../modules/Benefits/paths";
import { ENGAGEMENT_TYPES_ROOT_PATH } from "../../../modules/EngagementTypes/paths";
import { ENGAGEMENT_REQUIREMENTS_ROOT_PATH } from "../../../modules/RequirementDefinitions/paths/constants";
import { PATH_TAX_WITHHOLDINGS } from "../../../modules/TaxWithholdings/paths";
import {
  routeHelpersWorkLogsPayee,
  routeHelpersWorkLogsPayer
} from "../../../modules/WorkLogs/paths";
import { TAX_FILING_1099_FILING_PATH } from "../../../modules/TaxFiling/paths";

export enum ExpandableSideNavMenuOptions {
  GetPaid = "GetPaid",
  SendPayments = "SendPayments",
  FinancialBenefits = "FinancialBenefits",
  LifestyleBenefits = "LifestyleBenefits",
  Reporting = "Reporting",
  TaxFiling = "TaxFilings",
  DataAndIntegrations = "DataAndIntegrations",
  Settings = "Settings"
}

export type DefaultNavProps = {
  onExpand: (value: ExpandableSideNavMenuOptions) => void;
  isSideNavOpened: boolean;
  setIsDrawerOpened: (v: boolean) => void;
  setIsSideNavOpened: (v: boolean) => void;
  expandedItems: ExpandableSideNavMenuOptions[];
};

export const DefaultNav: React.FC<DefaultNavProps> = ({
  onExpand,
  expandedItems,
  setIsDrawerOpened,
  isSideNavOpened,
  setIsSideNavOpened
}) => {
  const isMobile = useIsMobile();
  const focusedFeatures = useFocusedFeatures();
  const queryNotifications = useQueryNotifications();
  const { terminology } = useCustomization();

  return (
    <>
      {focusedFeatures.showSideNavNew && (
        <New
          isSideNavOpened={isSideNavOpened}
          setIsSideNavOpened={isMobile ? setIsDrawerOpened : setIsSideNavOpened}
        />
      )}

      <SideNavRootLinkOption
        isVisible={focusedFeatures.showSideNavGettingStarted}
        to="/member/dashboard"
        testId="memberGettingStarted"
        label="Home"
        icon="home"
        isSideNavOpened={isSideNavOpened}
        count={
          queryNotifications.data &&
          selectorCountOfVisibleNotifications(queryNotifications.data)
        }
      />

      <WSFlexBox direction="column" className={styles.topNavItems} mt="M">
        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavGetPaid}
          isSideNavOpened={isSideNavOpened}
          icon="in"
          label="Get paid"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.GetPaid
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.GetPaid)}
        >
          <SideNavMenuItem
            linkTo="/member/invoices"
            testId="paymentsInvoicing"
            isVisible={focusedFeatures.showSideNavGetPaidInvoices}
            label={pluralize.plural(terminology().getPaidInvoice)}
            exact
          />

          <SideNavMenuItem
            linkTo={routeHelpersWorkLogsPayee.path}
            testId="workLogsPayee"
            isVisible={focusedFeatures.showSideNavWorkLogsPayee}
            label="Work logs"
          />

          <SideNavMenuItem
            linkTo="/member/payers"
            testId="clients"
            isVisible={focusedFeatures.showSideNavGetPaidClients}
            label={pluralize.plural(terminology().getPaidClient)}
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavSendPayments}
          isSideNavOpened={isSideNavOpened}
          icon="out"
          label="Send payments"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.SendPayments
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.SendPayments)}
        >
          <SideNavMenuItem
            linkTo="/member/invoices/payables"
            testId="paymentsPayables"
            isVisible={focusedFeatures.showSideNavPayables}
            label={pluralize.plural(terminology().sendPaymentsPayable)}
          />

          <SideNavMenuItem
            linkTo={routeHelpersWorkLogsPayer.path}
            testId="workLogsPayer"
            isVisible={focusedFeatures.showSideNavWorkLogsPayer}
            label="Work logs"
          />

          <SideNavMenuItem
            linkTo="/member/payees"
            testId="collaborators"
            isVisible={focusedFeatures.showSideNavPayees}
            label={pluralize.plural(terminology().sendPaymentsContractor)}
          />
          <SideNavMenuItem
            linkTo={ENGAGEMENT_TYPES_ROOT_PATH}
            testId="engagementTypes"
            isVisible={focusedFeatures.showSideNavEngagementRequirements}
            label="Engagements"
          />
          <SideNavMenuItem
            linkTo={ENGAGEMENT_REQUIREMENTS_ROOT_PATH}
            testId="collaborators"
            isVisible={focusedFeatures.showSideNavEngagementRequirements}
            label="Engagement requirements"
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavFinancialBenefits}
          isSideNavOpened={isSideNavOpened}
          icon="card"
          label="Financial benefits"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.FinancialBenefits
          )}
          onClick={() =>
            onExpand(ExpandableSideNavMenuOptions.FinancialBenefits)
          }
        >
          <SideNavMenuItem
            linkTo="/member/wallet"
            testId="myFinancesWallet"
            isVisible={focusedFeatures.showSideNavWallet}
            label="Wingspan wallet"
          />

          <SideNavMenuItem
            linkTo="/member/bookkeeping"
            testId="myFinancesIncomeAndExpenses"
            isVisible={focusedFeatures.showSideNavIncomeAndExpenses}
            label="Income & expenses"
          />

          <SideNavMenuItem
            linkTo={PATH_TAX_WITHHOLDINGS}
            testId="myFinancesTaxWithholdings"
            // selectorCanEnableTaxWithholdings (v1 monitored-entity + member)
            // could not be matched to v3; gate on the scope-based flag only.
            isVisible={focusedFeatures.showSideNavTaxWithholdings}
            label="Tax withholdings"
          />
        </CollapsibleMenu>

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavLifestyleBenefits}
          isSideNavOpened={isSideNavOpened}
          icon="heart"
          label="Lifestyle benefits"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.LifestyleBenefits
          )}
          onClick={() =>
            onExpand(ExpandableSideNavMenuOptions.LifestyleBenefits)
          }
        >
          <SideNavMenuItem
            linkTo={PATH_BENEFITS_AXIS_INSURANCE}
            testId="benefits-axis-insurance"
            isVisible={focusedFeatures.showAxisBenefits}
            label="Axis insurance"
          />

          <SideNavMenuItem
            linkTo={PATH_BENEFITS_INSURANCE}
            testId="benefits-additional-coverage"
            isVisible={focusedFeatures.showBenefits}
            label="Insurance"
          />

          <SideNavMenuItem
            linkTo={PATH_BENEFITS_PERKS}
            testId="benefits-my-benefits"
            isVisible={focusedFeatures.showBenefits}
            label="Premium perks"
          />

          <SideNavMenuItem
            linkTo="/member/knowledge-center"
            testId="knowledgeCenter"
            isVisible={true}
            label="Knowledge center"
            exact={true}
          />
        </CollapsibleMenu>

        <SideNavRootLinkOption
          isVisible={focusedFeatures.showSideNavReports}
          to="/member/reports"
          testId="reports"
          label="Reporting"
          icon="report"
          isSideNavOpened={isSideNavOpened}
        />

        <CollapsibleMenu
          isVisible={focusedFeatures.showSideNavTaxFillingSection}
          isSideNavOpened={isSideNavOpened}
          icon="paperclip"
          label="Tax filing"
          isExpanded={expandedItems.includes(
            ExpandableSideNavMenuOptions.TaxFiling
          )}
          onClick={() => onExpand(ExpandableSideNavMenuOptions.TaxFiling)}
        >
          <SideNavMenuItem
            linkTo={TAX_FILING_1099_FILING_PATH}
            testId="NEC1099Filing"
            isVisible={true}
            label="1099 Filing"
          />
        </CollapsibleMenu>
      </WSFlexBox>
    </>
  );
};
